import React from 'react'
import {
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  DropdownMenu,
  DropdownItem,
  InputGroupButtonDropdown,
  DropdownToggle,
  Button,
} from 'reactstrap'
import { DatePickerProps, DatePicker } from './DatePicker'
import { isSameMinute, isBefore } from 'date-fns'
import { Popover } from '../Popover/Popover'
import TimePicker, { TimePickerProps } from '../TimePicker/TimePicker'
import LocaleUtils, { TIME_FORMATS } from './localeUtils'
import intl from 'react-intl-universal'

// eslint-disable-next-line @typescript-eslint/no-var-requires
const css = require('./DatePickerInput.scss')

export interface LabelDate {
  date: Date
  label?: string
}

interface DatePickerInputProps extends DatePickerProps {
  /**
   * Called when a new date is submitted
   */
  onChange: (value: Date) => void
  /**
   * Input placeholder value. Not needed if this picker will never have a non-null
   * value
   */
  placeholder?: string
  /**
   * Determines if the timepicker is shown
   * @default true
   */
  showTime?: boolean
  /**
   * @see TimePicker
   */
  use24Hour?: TimePickerProps['use24Hour']
  /**
   * If a list of dates is provided, a button with an array of dates is shown for
   * quick date selection
   */
  quickDates?: (Date | LabelDate)[]
  /**
   * In order to prevent the date from changing in the input field before a new date is selected,
   * the DatePickerInput needs to control this prop. This behavior is not great and may change with new
   * designs.
   */
  onDayClick?: never
  inputId?: string
}

function isLabelDate(value: Date | LabelDate): value is LabelDate {
  return (value as LabelDate).date !== undefined
}

export function DatePickerInputSkeleton({
  placeholder,
}: Partial<DatePickerInputProps>) {
  return (
    <InputGroup className={css.datePickerInput}>
      <InputGroupAddon addonType="prepend">
        <InputGroupText>
          <i className="fa fa-calendar" />
        </InputGroupText>
      </InputGroupAddon>
      <Input disabled={true} placeholder={placeholder} />
    </InputGroup>
  )
}

/**
 * Controlled input that opens date picker when focused
 */
export function DatePickerInput({
  quickDates,
  onChange,
  showTime = true,
  value,
  placeholder = '',
  use24Hour,
  inputId,
  ...datePickerProps
}: DatePickerInputProps) {
  const [open, setOpen] = React.useState(false)
  const [unsubmittedDate, setUnsubmittedDate] = React.useState(value)

  /**
   * Uses formats from date-fns locales, if no value is provided for use24Hour, then
   * fallback to the current locale
   */
  const getDisplay = (date: Date) => {
    const formatTime =
      use24Hour === undefined
        ? LocaleUtils.locale.formatLong.time({ width: 'short' })
        : use24Hour
        ? TIME_FORMATS.hour24
        : TIME_FORMATS.hour12
    const formatDate = LocaleUtils.locale.formatLong.date({ width: 'medium' })
    return isSameMinute(date, LocaleUtils.relativeNow())
      ? intl.get('datePicker.now')
      : LocaleUtils.cleanFormat(date, `${formatDate} - ${formatTime}`)
  }

  let errorMessage = ''
  if (
    datePickerProps.disabledDays &&
    'before' in datePickerProps.disabledDays &&
    isBefore(unsubmittedDate, datePickerProps.disabledDays.before) &&
    !isSameMinute(unsubmittedDate, datePickerProps.disabledDays.before)
  ) {
    errorMessage = intl.get('datePicker.pastError')
  }

  return !LocaleUtils.locale ? (
    <DatePickerInputSkeleton placeholder={placeholder} />
  ) : (
    <InputGroup
      className={`bootstrap4 ${css.datePickerInput}`}
      data-testid="datepicker-input"
    >
      <Popover
        placement="bottom"
        content={({ close }) => (
          <>
            {showTime && (
              <TimePicker
                value={unsubmittedDate}
                onChange={(dateWithUpdatedTime) => {
                  setUnsubmittedDate(
                    new Date(
                      unsubmittedDate.getFullYear(),
                      unsubmittedDate.getMonth(),
                      unsubmittedDate.getDate(),
                      dateWithUpdatedTime.getHours(),
                      dateWithUpdatedTime.getMinutes()
                    )
                  )
                }}
                use24Hour={use24Hour}
              />
            )}
            <DatePicker
              {...datePickerProps}
              value={unsubmittedDate}
              onDayClick={(dateWithUpdatedDay, { disabled }) => {
                if (disabled) return
                setUnsubmittedDate(
                  new Date(
                    dateWithUpdatedDay.getFullYear(),
                    dateWithUpdatedDay.getMonth(),
                    dateWithUpdatedDay.getDate(),
                    unsubmittedDate.getHours(),
                    unsubmittedDate.getMinutes()
                  )
                )
              }}
              localeUtils={LocaleUtils.localeUtils}
              labels={{
                previousMonth: intl.get('datePicker.previousMonth'),
                nextMonth: intl.get('datePicker.nextMonth'),
              }}
            />
            <Button
              onClick={() => {
                setUnsubmittedDate(value) // reset date & time pickers
                close()
              }}
            >
              {intl.get('datePicker.cancel')}
            </Button>
            <Button
              color="primary"
              style={{ float: 'right' }}
              disabled={Boolean(errorMessage)}
              onClick={() => {
                onChange(unsubmittedDate)
                close()
              }}
            >
              {intl.get('datePicker.submit')}
            </Button>

            {errorMessage && (
              <p className="m-0 pt-2 text-danger text-right">{errorMessage}</p>
            )}
          </>
        )}
      >
        {({ ref, onClick }) => (
          <>
            <InputGroupAddon addonType="prepend" onClick={onClick}>
              <InputGroupText>
                <i className="fa fa-calendar" />
              </InputGroupText>
            </InputGroupAddon>
            <Input
              className="real-input"
              readOnly={true}
              value={getDisplay(value)}
              onFocus={onClick}
              innerRef={ref}
              placeholder={placeholder}
              id={inputId}
            />
          </>
        )}
      </Popover>

      {quickDates && (
        <InputGroupButtonDropdown
          addonType="append"
          isOpen={open}
          toggle={() => setOpen(!open)}
        >
          <DropdownToggle
            caret={false}
            className="quickDates"
            data-testid="quickpick-dates"
          >
            <i className="fa fa-ellipsis-h" />
          </DropdownToggle>
          <DropdownMenu right={true}>
            {quickDates.map((d, index) => {
              const date = isLabelDate(d) ? d.date : d

              return (
                <DropdownItem key={index} onClick={() => onChange(date)}>
                  {getDisplay(date)}&nbsp;
                  {isLabelDate(d) && d.label && `(${d.label})`}
                </DropdownItem>
              )
            })}
          </DropdownMenu>
        </InputGroupButtonDropdown>
      )}
    </InputGroup>
  )
}
